import { isMedicalLinkHostname } from '../api/apiConfig'
import type { ChatMessage } from '../types/chat'

/** MediKaLLink in-app menu path: `/medecin/{uuid}/{menuRoute}`. */
const MEDILINK_MENU_PATH = /^\/medecin\/[0-9a-fA-F-]{36}\/[a-zA-Z0-9][a-zA-Z0-9_-]*\/?$/i

/** Patient dossier: `/medecin/{uuid}/patient/dossier/{patientId}` (one path segment for id). */
const MEDILINK_PATIENT_DOSSIER_PATH =
  /^\/medecin\/[0-9a-fA-F-]{36}\/patient\/dossier\/[^/]+\/?$/i

/** TEMPORARY TESTING: mock patient page when ``MOCK_PATIENT_PAGE`` is enabled on the API. */
const MEDILINK_MOCK_PATIENT_PATH = /^\/medecin\/[0-9a-fA-F-]{36}\/patient\/mock\/?$/i

export type FirstIntentNavigationKind = 'patient_dossier' | 'page_navigation'

/** Same priority as {@link pickFirstIntentUrl} (first assistant row wins in array order). */
export function pickFirstIntentNavigation(
  msgs: ChatMessage[],
): { kind: FirstIntentNavigationKind; url: string } | undefined {
  for (const m of msgs) {
    if (m.historySnapshot) continue
    const dossier = (m.openPatientDossierUrl ?? '').trim()
    if (dossier) return { kind: 'patient_dossier', url: dossier }
    const nav = (m.openNavigationUrl ?? '').trim()
    if (nav) return { kind: 'page_navigation', url: nav }
  }
  return undefined
}

/** Latest assistant intent URL (newest row wins — used after cross-patient switch). */
export function pickLatestIntentNavigation(
  msgs: readonly ChatMessage[],
): { kind: FirstIntentNavigationKind; url: string } | undefined {
  for (let i = msgs.length - 1; i >= 0; i--) {
    const m = msgs[i]
    if (m.historySnapshot) continue
    const dossier = (m.openPatientDossierUrl ?? '').trim()
    if (dossier) return { kind: 'patient_dossier', url: dossier }
    const nav = (m.openNavigationUrl ?? '').trim()
    if (nav) return { kind: 'page_navigation', url: nav }
  }
  return undefined
}

/** Navigate host to the latest patient dossier URL in chat (same-tab MediKaLLink rules). */
export function navigateLatestPatientDossierInHost(msgs: readonly ChatMessage[]): boolean {
  const pick = pickLatestIntentNavigation(msgs)
  if (pick?.kind !== 'patient_dossier' || !pick.url) return false
  return tryNavigateMedilinkMenuSameTab(pick.url)
}

function currentHostLocationHref(): string | undefined {
  try {
    return (window.top?.location ?? window.location).href
  } catch {
    try {
      return window.location.href
    } catch {
      return undefined
    }
  }
}

/** Same MediKaLLink in-app path (ignore trailing slash and query/hash). */
function medilinkInAppUrlsEquivalent(a: string, b: string): boolean {
  try {
    const pa = new URL(a)
    const pb = new URL(b)
    return (
      pa.protocol === pb.protocol &&
      pa.hostname.toLowerCase() === pb.hostname.toLowerCase() &&
      pa.pathname.replace(/\/$/, '') === pb.pathname.replace(/\/$/, '')
    )
  } catch {
    return a === b
  }
}

function reloadHostLocation(): boolean {
  try {
    window.top.location.reload()
    return true
  } catch {
    try {
      window.location.reload()
      return true
    } catch {
      return false
    }
  }
}

/**
 * Navigate the host tab to a MediKaLLink in-app URL (same tab): menu routes or patient dossier.
 * When the target URL is already open, force a full reload so re-requesting the same patient refreshes the chart.
 */
export function tryNavigateMedilinkMenuSameTab(url: string): boolean {
  const u = (url || '').trim()
  if (!u) return false
  try {
    const parsed = new URL(u)
    if (parsed.protocol !== 'https:') return false
    if (!isMedicalLinkHostname(parsed.hostname)) return false
    const pathOk =
      MEDILINK_MENU_PATH.test(parsed.pathname) ||
      MEDILINK_PATIENT_DOSSIER_PATH.test(parsed.pathname) ||
      MEDILINK_MOCK_PATIENT_PATH.test(parsed.pathname)
    if (!pathOk) return false
    const current = currentHostLocationHref()
    if (current && medilinkInAppUrlsEquivalent(current, u)) {
      return reloadHostLocation()
    }
    try {
      window.top.location.assign(u)
    } catch {
      try {
        window.location.assign(u)
      } catch {
        return false
      }
    }
    return true
  } catch {
    return false
  }
}

/** First HTTPS intent URL on assistant rows (dossier or in-app page). */
export function pickFirstIntentUrl(msgs: ChatMessage[]): string | undefined {
  for (const m of msgs) {
    if (m.historySnapshot) continue
    if (m.openPatientDossierUrl) return m.openPatientDossierUrl
    if (m.openNavigationUrl) return m.openNavigationUrl
  }
  return undefined
}

/**
 * Open a blank tab synchronously during a user gesture (Send, patient pick, or mic start).
 * After the API returns, call {@link navigateOrClosePendingWindow} with the real URL.
 *
 * Important: do **not** pass `noopener` in the third argument — in Chrome and Firefox that
 * makes `window.open` return `null`, so the tab cannot be targeted with `location.replace`
 * and intent URLs never load.
 */
export function openBlankTabForIntentGesture(): Window | null {
  try {
    const w = window.open('about:blank', '_blank')
    if (w) {
      try {
        w.opener = null
      } catch {
        // ignore
      }
    }
    return w
  } catch {
    return null
  }
}

function tryOpenUrlInNewTab(url: string): Window | null {
  try {
    const win = window.open(url, '_blank')
    if (win) {
      try {
        win.opener = null
      } catch {
        // ignore
      }
    }
    return win
  } catch {
    return null
  }
}

/** Last resort when window.open returns null (some policies still allow navigation). */
function tryOpenUrlViaAnchorClick(url: string): void {
  try {
    const a = document.createElement('a')
    a.href = url
    a.target = '_blank'
    a.rel = 'noopener noreferrer'
    a.style.display = 'none'
    document.body.appendChild(a)
    a.click()
    a.remove()
  } catch {
    // ignore
  }
}

/**
 * Point the gesture-opened window at `url`, or close it if there is no intent URL.
 * If no window (popup blocked at gesture time), fall back to opening `url` in a new tab — often blocked too
 * because transcript completion is not a user gesture.
 *
 * @returns whether we could navigate an existing window or open a new one with a non-null `window.open` handle.
 *   `false` usually means the user should use a manual "Open link" control.
 */
export function navigateOrClosePendingWindow(w: Window | null, url: string | undefined): boolean {
  if (!url) {
    if (w && !w.closed) {
      try {
        w.close()
      } catch {
        // ignore
      }
    }
    return true
  }
  if (!w || w.closed) {
    const win = tryOpenUrlInNewTab(url)
    if (win) return true
    tryOpenUrlViaAnchorClick(url)
    return false
  }
  try {
    // Prefer assign: some environments behave more reliably than replace for cross-origin navigations.
    w.location.assign(url)
    return true
  } catch {
    try {
      w.location.replace(url)
      return true
    } catch {
      try {
        w.close()
      } catch {
        // ignore
      }
      const win = tryOpenUrlInNewTab(url)
      if (win) return true
      tryOpenUrlViaAnchorClick(url)
      return false
    }
  }
}
